import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { EmploymentType, GroupBy } from '../workforce.types';

export interface WorkforceFilters {
  groupBy: GroupBy;
  startMonth: string;
  endMonth: string;
  employmentType: EmploymentType;
}

@Component({
  selector: 'app-filters',
  standalone: false,
  templateUrl: './filters.component.html',
  styles: [`.filter-controls{display:flex;align-items:center;gap:10px;margin-bottom:20px}.filter-trigger,.clear-active-filter{height:42px;border-radius:8px;padding:0 17px;font:600 14px inherit;cursor:pointer}.filter-trigger{border:1px solid #60259e;background:#fff;color:#60259e}.filter-trigger span{font-size:20px;vertical-align:-2px;margin-right:6px}.clear-active-filter{border:1px solid #e04a4a;background:#fff;color:#d72d2d}.filter-backdrop{position:fixed;inset:0;z-index:1000;background:#0005;display:grid;place-items:center;padding:20px}.filter-dialog{position:relative;width:700px;max-width:calc(100vw - 40px);min-width:600px;padding:24px;background:#fff;border-radius:10px;box-shadow:0 12px 40px #0003;color:#34354f}.filter-dialog h2{font-size:16px;font-weight:400;color:#393939;margin:0;padding:0 0 21px}.filter-close{position:absolute;right:24px;top:20px;border:0;background:none;color:#60259e;font-size:26px;line-height:1;cursor:pointer}.filter-fields{display:grid;grid-template-columns:1fr 1fr;gap:24px 32px}.filter-field{display:flex;flex-direction:column;gap:8px}.filter-label{display:flex;align-items:center;gap:6px;color:#4b5563;font-size:13px;font-weight:400}.filter-field select,.filter-field input{width:100%;height:44px;border:1px solid #d1d5db;border-radius:6px;background:#fff;padding:0 12px;color:#374151;font:14px inherit;transition:all .2s ease}.filter-field.date-field input{height:36px;padding:0 16px}.filter-field select:focus,.filter-field input:focus{outline:none;border-color:#60259e;box-shadow:0 0 0 3px rgba(96,37,158,.1)}.filter-field input:invalid{border-color:#dc3545}.info-tip{position:relative;display:inline-grid;place-items:center;width:16px;height:16px;border:1px solid #60259e;border-radius:50%;color:#60259e;background:#fff;font-size:11px;font-style:normal;font-weight:600;line-height:1;cursor:help}.info-tip::after{content:attr(data-tooltip);position:absolute;z-index:3;left:50%;bottom:calc(100% + 8px);width:max-content;max-width:230px;padding:7px 9px;border-radius:5px;background:#34354f;color:#fff;font-size:12px;font-weight:400;line-height:1.35;opacity:0;pointer-events:none;transform:translateX(-50%);transition:opacity .15s}.info-tip:hover::after,.info-tip:focus::after{opacity:1}.filter-actions{display:flex;justify-content:flex-end;gap:12px;padding:21px 0 0}.filter-actions button{min-width:120px;padding:10px 20px;border-radius:6px;font:400 13px inherit;cursor:pointer}.filter-actions .clear-button{border:1px solid #ef4444;color:#ef4444;background:#fff}.filter-actions .apply-button{border:1px solid #60259e;background:#60259e;color:#fff}.filter-actions .apply-button:disabled{opacity:.55;cursor:not-allowed}@media(max-width:640px){.filter-dialog{width:95vw;min-width:0;max-width:95vw;padding:24px}.filter-fields{grid-template-columns:1fr;gap:20px}.filter-actions{flex-direction:column}.filter-actions button{width:100%}}`],
  styleUrls: ['./filters.component.css'],
})
export class FiltersComponent implements OnChanges {
  @Input() idPrefix = '';
  @Input() groupBy: GroupBy = 'department';
  @Input() startMonth = '';
  @Input() endMonth = '';
  @Input() employmentType: EmploymentType = '';
  @Input() onRollOnly = false;
  @Output() filtersApply = new EventEmitter<WorkforceFilters>();
  @Output() filtersClear = new EventEmitter<void>();

  isOpen = false;
  draftGroupBy: GroupBy = 'department';
  draftEmploymentType: EmploymentType = '';
  draftStartDate = '';
  draftEndDate = '';

  get maxDate(): string {
    const today = new Date();
    return this.asIsoDate(new Date(today.getFullYear(), today.getMonth(), 0));
  }

  get hasAppliedFilters(): boolean {
    return this.groupBy !== 'department' || Boolean(this.startMonth || this.endMonth || this.employmentType !== (this.onRollOnly ? 'On-Roll' : ''));
  }

  get invalidRange(): boolean {
    return Boolean(this.draftStartDate && this.draftEndDate && this.draftStartDate > this.draftEndDate);
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.isOpen) this.copyAppliedFilters();
  }

  open(): void {
    this.copyAppliedFilters();
    this.isOpen = true;
  }

  apply(): void {
    if (this.invalidRange) return;
    this.filtersApply.emit({
      groupBy: this.draftGroupBy,
      employmentType: this.onRollOnly ? 'On-Roll' : this.draftEmploymentType,
      startMonth: this.toMonth(this.draftStartDate),
      endMonth: this.toMonth(this.draftEndDate),
    });
    this.isOpen = false;
  }

  clear(): void {
    this.filtersClear.emit();
    this.copyAppliedFilters();
    this.isOpen = false;
  }

  onStartDate(value: string): void {
    this.draftStartDate = value;
    if (this.draftEndDate && value > this.draftEndDate) this.draftEndDate = '';
  }

  private copyAppliedFilters(): void {
    this.draftGroupBy = this.groupBy;
    this.draftEmploymentType = this.onRollOnly ? 'On-Roll' : this.employmentType;
    this.draftStartDate = this.monthToDate(this.startMonth, false);
    this.draftEndDate = this.monthToDate(this.endMonth, true);
  }

  private monthToDate(month: string, endOfMonth: boolean): string {
    if (!month) return '';
    const [year, monthNumber] = month.split('-').map(Number);
    if (!year || !monthNumber) return '';
    return this.asIsoDate(new Date(year, monthNumber - (endOfMonth ? 0 : 1), endOfMonth ? 0 : 1));
  }

  private toMonth(date: string): string { return date ? date.slice(0, 7) : ''; }

  private asIsoDate(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }
}
